import { Tables } from '@/types/database.types';
import { getMontrealNow, parseAsMontrealTime } from '@/utils/date-format-montreal';

type Festival = Tables<'festivals'>;

export interface TicketSalesStatus {
    areOnSale: boolean;
    reason: 'not_started' | 'ended' | 'no_dates_set' | 'on_sale';
    startDate: Date | null;
    endDate: Date | null;
}

/**
 * Check if tickets are currently on sale for a festival
 * @param festival - The festival object from the database
 * @returns TicketSalesStatus object with sale status and details
 */
export function getTicketSalesStatus(festival: Festival | null): TicketSalesStatus {
    if (!festival) {
        return {
            areOnSale: false,
            reason: 'no_dates_set',
            startDate: null,
            endDate: null,
        };
    }

    const now = getMontrealNow();

    // No start date = tickets not on sale
    if (!festival.ticket_sales_start_date_and_time) {
        return {
            areOnSale: false,
            reason: 'no_dates_set',
            startDate: null,
            endDate: null,
        };
    }

    // Parse dates - database returns ISO format with timezone (e.g., "2025-12-11 04:59:00+00")
    // Normalize format: replace space with T for proper ISO 8601 format (some browsers require T)
    const normalizeDateString = (dateStr: string): string => {
        // Replace space between date and time with T if present
        return dateStr.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})/, '$1T$2');
    };

    const startDateStr = normalizeDateString(festival.ticket_sales_start_date_and_time);
    const startDate = parseAsMontrealTime(startDateStr);
    const endDate = festival.ticket_sales_end_date_and_time
        ? parseAsMontrealTime(normalizeDateString(festival.ticket_sales_end_date_and_time))
        : null;

    // Validate parsed dates
    if (isNaN(startDate.getTime())) {
        console.error('Invalid ticket_sales_start_date_and_time:', festival.ticket_sales_start_date_and_time);
        return {
            areOnSale: false,
            reason: 'no_dates_set',
            startDate: null,
            endDate: null,
        };
    }

    // If endDate parsing failed, treat as null (no end date restriction)
    const validEndDate = endDate && !isNaN(endDate.getTime()) ? endDate : null;
    if (endDate && isNaN(endDate.getTime())) {
        console.error('Invalid ticket_sales_end_date_and_time:', festival.ticket_sales_end_date_and_time);
    }

    // Before start date
    if (now < startDate) {
        return {
            areOnSale: false,
            reason: 'not_started',
            startDate,
            endDate: validEndDate,
        };
    }

    // After end date (if set)
    if (validEndDate && now > validEndDate) {
        return {
            areOnSale: false,
            reason: 'ended',
            startDate,
            endDate: validEndDate,
        };
    }

    // Tickets are on sale
    return {
        areOnSale: true,
        reason: 'on_sale',
        startDate,
        endDate: validEndDate,
    };
}

/**
 * Simple boolean check if tickets are currently on sale
 * @param festival - The festival object from the database
 * @returns true if tickets are on sale, false otherwise
 */
export function areTicketsOnSale(festival: Festival | null): boolean {
    return getTicketSalesStatus(festival).areOnSale;
}

/**
 * Calculate time remaining until ticket sales start
 * @param startDate - The ticket sales start date
 * @returns Object with days, hours, and minutes remaining
 */
export function getTimeUntilSales(startDate: Date): {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
} {
    const now = getMontrealNow();
    const diff = startDate.getTime() - now.getTime();

    if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
}

/**
 * Check if a festival is expired based on end_date or year
 * If ticket sales are still active, the festival is not considered expired for ticket purchasing
 * @param festival - The festival object from the database
 * @returns true if the festival is expired, false otherwise
 */
export function isFestivalExpired(festival: Festival | null): boolean {
    if (!festival) {
        return false;
    }

    // If tickets are still on sale, festival is not expired for ticket purchasing purposes
    if (areTicketsOnSale(festival)) {
        return false;
    }

    const now = getMontrealNow();
    const currentYear = now.getFullYear();

    // Check if festival year is less than current year
    if (festival.year < currentYear) {
        return true;
    }

    // Check if end_date exists and is in the past
    if (festival.end_date) {
        try {
            // Parse end_date as Montreal timezone to ensure consistent comparison
            // festival.end_date is always a string (from database), so we can safely use it
            let dateString = festival.end_date;
            
            // If it's just a date (YYYY-MM-DD), append time to make it end of day in Montreal
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                dateString = `${dateString}T23:59:59`;
            }
            
            const endDate = parseAsMontrealTime(dateString);
            
            // Check if date is valid
            if (isNaN(endDate.getTime())) {
                return false;
            }
            
            // Ensure it's end of day (23:59:59.999) in Montreal timezone
            // Since parseAsMontrealTime converts to UTC, we need to set UTC hours
            // 23:59:59 Montreal = 23:59:59 + offset in UTC
            // Montreal EST is UTC-5 (5 hours behind), so 23:59 EST = 04:59 UTC next day
            // Montreal EDT is UTC-4 (4 hours behind), so 23:59 EDT = 03:59 UTC next day
            const month = endDate.getUTCMonth(); // 0-11
            const isDST = month >= 2 && month <= 10; // March (2) to November (10)
            const montrealOffsetHours = isDST ? 4 : 5; // Montreal is behind UTC
            endDate.setUTCHours(23 + montrealOffsetHours, 59, 59, 999);
            
            if (now > endDate) {
                return true;
            }
        } catch (error) {
            // If parsing fails, fall back to simple date comparison to prevent crashes
            console.error('Error parsing festival end_date:', error);
            try {
                const fallbackDate = new Date(festival.end_date);
                if (!isNaN(fallbackDate.getTime())) {
                    fallbackDate.setHours(23, 59, 59, 999);
                    if (now > fallbackDate) {
                        return true;
                    }
                }
            } catch {
                // If even fallback fails, assume festival is not expired
                return false;
            }
        }
    }

    return false;
}
